<template>
  <ValidationObserver v-slot="{ handleSubmit, invalid ,reset, errors}" ref="observer" slim>
    <b-tab>
      <template v-slot:title>
        {{ $t('Item Locations') }}
      </template>
      <div class="outer-container pt-0">
        <div v-if="selectedBranch">
          <div v-if="itemLocationsLoading" class="mt-4">
            <spinner show/>
          </div>
          <div v-else>
            <div>
              <div class="row d-flex justify-content-end pr-2">
                <div class="text-right">
                  <b-button class="pr-0 pb-0" variant="link" @click="onPrintAllQRCodesClick"
                            :disabled="bulkQrGenerationLoading">
                    <font-awesome-icon v-if="!bulkQrGenerationLoading" icon="qrcode" class="icon"
                                       style="font-size: .9em"/>
                    <b-spinner v-else style="width: 1rem; height: 1rem"/>
                    {{ $t('Export QR Codes') }}
                  </b-button>
                </div>
                <div class="text-right">
                  <b-button class="pr-0 pb-0" variant="link" @click="onNewItemLocationClick">
                    <font-awesome-icon :icon="'plus'" style="font-size: .9em"></font-awesome-icon>
                    {{ $t('New Item Location') }}
                  </b-button>
                </div>
              </div>
            </div>
            <v-jstree text-field-name="name" value-field-name="uuid" ref="myTree" size="small" :data="jsTreeData"
                      draggable @item-drop="onDragEnd" @item-click="itemClick">
              <template scope="item">
                <div style="width: 300px" class="tree-anchor-child d-flex align-items-center">
                <span class="ml-2 d-flex align-items-center flex-wrap">
                  {{ item.model.name }}
                  <span v-if="item.model.is_primary" class="badge badge-primary ml-2">{{ $t('Primary') }}</span>
                  <span v-if="item.model.is_released" class="badge badge-secondary ml-2">{{ $t('Released') }}</span>
                </span>
                  <span class="ml-auto mr-2 location-actions">
                  <font-awesome-icon icon="qrcode" class="icon text-primary mr-2" @click="onQrCodeClick($event, item)"/>
                  <font-awesome-icon icon="pen" class="icon text-primary mr-2" @click="onEditClick($event, item)"/>
                  <font-awesome-icon icon="plus" class="icon text-primary mr-2" @click="onAddChildClick($event, item)"/>
                  <font-awesome-icon icon="trash" class="icon text-danger" @click="onDeleteClick($event, item)"/>
                </span>
                </div>
              </template>
            </v-jstree>
            <div v-if="jsTreeData.length === 0">
              <no-data-available class="mt-4"/>
            </div>
          </div>
        </div>
        <no-data-available :height="5" v-if="!selectedBranch"/>
      </div>
    </b-tab>
  </ValidationObserver>
</template>

<script>

  import {createNamespacedHelpers} from "vuex";
  import NoDataAvailable from "../../../../../core/components/common/NoDataAvailable";
  import {SOURCE_BRANCH_ITEM_LOCATIONS} from "@/modules/constants";
  import BranchService from "../../BranchService";
  import {
    ON_PDF_MODAL_DATA_RETRIEVE,
    ON_PDF_MODAL_OPEN,
  } from "@/plugins/event-bus/event-bus-names";
  import ItemLocationService from "@/modules/setup/branch/ItemLocationService";
  import VJstree from 'vue-jstree'
  import Spinner from "@/core/components/common/Spinner";

  const {mapState, mapActions} = createNamespacedHelpers('setup/branch');

  export default {
    name: "ItemLocations",
    components: {Spinner, NoDataAvailable, VJstree},
    data() {
      return {
        bulkQrGenerationLoading: false,
        jsTreeData: []
      };
    },
    computed: {
      ...mapState(['selectedBranch', 'itemLocationsLoading', 'itemLocations']),
    },
    watch: {
      selectedBranch() {
        if (this.selectedBranch) {
          this.getItemLocations()
        }
      },
      itemLocations() {
        if (this.itemLocations && this.itemLocations.length > 0) {
          this.initTree(this.itemLocations);
        } else {
          this.jsTreeData = [];
        }
      }
    },
    methods: {
      ...mapActions(['showItemLocationModal', 'removeItemLocation', 'getItemLocations', 'updateItemLocation']),
      async onPrintAllQRCodesClick() {
        let iterateForBulkQr = (item, data) => {
          data[item.uuid] = BranchService.getItemLocationUrlForQrCode(this.selectedBranch.uuid, item.uuid)
          if (item.children) {
            for (let child of item.children) {
              iterateForBulkQr(child, data)
            }
          }
        }
        this.bulkQrGenerationLoading = true;
        let data = {};
        for (let item of this.itemLocations) {
          iterateForBulkQr(item, data)
        }
        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: this.$i18n.t('{branch} Item Location QR Codes', {branch: this.selectedBranch.name})
        });
        let response = await ItemLocationService.getItemLocationsQrCodePdf({data});
        this.bulkQrGenerationLoading = false;
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            filename: this.$i18n.t('{branch} Item Location QR Codes', {branch: this.selectedBranch.name}),
            pdf_url: response.body.url
          })
        }
      },
      async onQrCodeClick($event, item) {
        $event.stopPropagation()

        this.$evBus.$emit(ON_PDF_MODAL_OPEN, {
          title: item.model.itemLocationInfo.name,
        });
        let data = {};
        data[item.model.itemLocationInfo.uuid] = BranchService.getItemLocationUrlForQrCode(this.selectedBranch.uuid, item.model.itemLocationInfo.uuid);
        let response = await ItemLocationService.getItemLocationsQrCodePdf({data});
        if (response.success) {
          this.$evBus.$emit(ON_PDF_MODAL_DATA_RETRIEVE, {
            pdf_url: response.body.url
          })
        }
      },
      onNewItemLocationClick() {
        this.showItemLocationModal({
          object: {branch_uuid: this.selectedBranch.uuid},
          source: SOURCE_BRANCH_ITEM_LOCATIONS
        })
      },
      onAddChildClick($event, item) {
        $event.stopPropagation()
        this.showItemLocationModal({
          object: {branch_uuid: this.selectedBranch.uuid},
          source: SOURCE_BRANCH_ITEM_LOCATIONS,
          parentItemLocation: item.model.itemLocationInfo
        })
      },
      onEditClick($event, item) {
        $event.stopPropagation();
        this.showItemLocationModal({
          object: item.model.itemLocationInfo,
          source: SOURCE_BRANCH_ITEM_LOCATIONS,
          parentItemLocation: item.model.level === 0 ? null : item.vm.$parent.model.itemLocationInfo
        });
      },
      onDeleteClick($event, item) {
        $event.stopPropagation();
        const location = item.model.itemLocationInfo;
        const message = location.is_released
          ? this.$i18n.t(
            'This location is set as the Released Location. Deleting it will clear the Released Location for this branch, so collected items will no longer be auto-moved here. Continue?'
          )
          : this.$i18n.t('Are you sure you want to delete following item location?');
        this.$confirm(
            message,
            this.$i18n.t('Item location has been successfully deleted'),
            this.removeItemLocation,
          {itemLocation: location, source: SOURCE_BRANCH_ITEM_LOCATIONS},
          this.$i18n.t('Please Confirm'),
          location.is_released ? this.$i18n.t('Continue') : this.$i18n.t('Yes'),
          this.$i18n.t(location.is_released ? 'Cancel' : 'No'),
        );
      },
      async onDragEnd(node, item, draggedItem) {
        let requestData = {
          parent_uuid: item.itemLocationInfo.uuid,
          uuid: draggedItem.itemLocationInfo.uuid,
          branch_uuid: draggedItem.itemLocationInfo.branch_uuid,
          is_primary: draggedItem.itemLocationInfo.is_primary,
          is_released: draggedItem.itemLocationInfo.is_released,
        }
        this.updateItemLocation({requestData, changeBranchState: false})
      },
      initTree(data) {
        if (!data || data.length === 0) {
          return false;
        }

        let tmpData = data;
        for (let rootItem of tmpData) {
          this.prepareDatForTree(rootItem)
        }

        this.jsTreeData = tmpData;
      },
      prepareDatForTree(item) {
        item.opened = true

        item.itemLocationInfo = {
          address: item.address,
          address_2: item.address_2,
          address_3: item.address_3,
          branch_uuid: item.branch_uuid,
          city: item.city,
          country_code: item.country_code,
          is_primary: item.is_primary,
          is_released: item.is_released,
          name: item.name,
          uuid: item.uuid,
          zip_code: item.zip_code,
          state: item.state,
          email: item.email,
          phone: item.phone,
        }
        // Keep flags on the tree node model for badges
        item.is_primary = item.is_primary
        item.is_released = item.is_released

        if (item.children) {
          for (let child of item.children) {
            this.prepareDatForTree(child)
          }
        }
      },
      itemClick(node, item) {
        this.$nextTick(() => {
          if (item.opened) {
            item.closeChildren()
          } else {
            item.openChildren()
          }
        })
      },
    }
  }
</script>

<style lang="scss" scoped>
  /deep/ .tree-anchor-child {

    &:hover {
      background-color: #f6f8fb;

      .location-actions {
        visibility: visible;
      }
    }

    .location-actions {
      visibility: hidden;
    }
  }
</style>
